import {
  autoSort,
  evaluateGrouping,
  isWild,
  solveHand,
  type CardId,
  type Group,
  type MeldContext,
} from '@rummy/engine';
import type { RoomView, TableView } from '@rummy/protocol';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CardFlights, FLIGHT_MS, type Flight } from '../components/CardFlights.tsx';
import { CenterStack } from '../components/CenterStack.tsx';
import { Countdown } from '../components/Countdown.tsx';
import { DealRitual } from '../components/DealRitual.tsx';
import { HandHistory } from '../components/HandHistory.tsx';
import { PlayingCard } from '../components/PlayingCard.tsx';
import { EMOTES, SeatCard } from '../components/Seat.tsx';
import { latestPublicMove, TableIntel } from '../components/TableIntel.tsx';
import { Tray } from '../components/Tray.tsx';
import { haptic } from '../feedback/haptics.ts';
import { interactionMetrics } from '../feedback/interactionMetrics.ts';
import { findHandHint } from '../game/handHints.ts';
import { reconcileGroups } from '../game/trayLayout.ts';
import { sounds } from '../audio/sfx.ts';
import { useIdle } from '../hooks/useIdle.ts';
import { useI18n, type MessageKey } from '../i18n/index.tsx';
import type { GameClient, PendingAction, PendingDraw } from '../net/client.ts';
import { RECONNECT_GRACE_MS, type ConnectionState } from '../net/client.ts';
import { DeckVerification } from './DeckVerification.tsx';
import { HandSummary } from './HandSummary.tsx';

/** How long without input before the ambient light holds still. */
const AMBIENT_IDLE_MS = 20_000;

/**
 * Where opponents sit around the felt on a wide screen, in degrees on the
 * oval: 180 is your left, 90 across from you, 0 your right. Turn order runs
 * the same way the row reads — the next player on your left, round over the
 * top to the one who plays just before you. Hand-placed rather than evenly
 * spread so two players face each other and three take left, across, right,
 * the way people sit at a real table.
 */
const ORBIT_ANGLES: Record<number, number[]> = {
  1: [90],
  2: [145, 35],
  3: [165, 90, 15],
  4: [175, 125, 55, 5],
  5: [180, 135, 90, 45, 0],
};

export interface TableScreenProps {
  table: TableView;
  room: RoomView;
  client: GameClient;
  accountId: string;
  confirmDiscard: boolean;
  quickPlay: boolean;
  expertMode: boolean;
  showTableIntel: boolean;
  showGameplayGuidance: boolean;
  sound: boolean;
  /** The your-turn chime. On by default and independent of `sound`. */
  turnAlert: boolean;
  /** Opt-in; requires Notification permission (SettingsPanel requests it). */
  turnNotifications: boolean;
  cardSize: 'compact' | 'comfortable' | 'large';
  connection: ConnectionState;
  /** Epoch ms the current outage started; 0 while connected (net/client.ts). */
  disconnectedAt: number;
  /** A card the server accepted before the full table snapshot is ready. */
  pendingDraw: PendingDraw | null;
  pendingAction: PendingAction | null;
  onLeave: () => void;
  /** Phones hide the app header at the table, so Settings lives in its menu. */
  onOpenSettings: () => void;
}

export function TableScreen({
  table,
  room,
  client,
  accountId,
  confirmDiscard,
  quickPlay,
  expertMode,
  showTableIntel,
  showGameplayGuidance,
  sound,
  turnAlert,
  turnNotifications,
  cardSize,
  connection,
  disconnectedAt,
  pendingDraw,
  pendingAction,
  onLeave,
  onOpenSettings,
}: TableScreenProps) {
  const { t, formatNumber } = useI18n();
  const ambientIdle = useIdle(AMBIENT_IDLE_MS);
  const pileRef = useRef<HTMLButtonElement>(null);
  const deckRef = useRef<HTMLDivElement>(null);
  const trayRef = useRef<HTMLDivElement>(null);
  const seatRefs = useRef(new Map<string, HTMLElement>());
  const flightId = useRef(0);
  const flightTimers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const [flights, setFlights] = useState<Flight[]>([]);

  // The table's rules come from the server: at two seats this is a single
  // deck, at three or more it is two (§3.1).
  const ctx = useMemo<MeldContext>(
    () => ({ config: table.rules, wildRank: table.wildRank }),
    [table.rules, table.wildRank],
  );

  const you = table.you;
  const authoritativeHand = useMemo(() => you?.hand ?? [], [you?.hand]);
  const pendingCard = pendingDraw?.handId === table.handId ? pendingDraw.card : null;
  // A draw acknowledgement is already committed by the server. Rendering its
  // card locally avoids holding the tray behind the more expensive all-seat
  // snapshot, while reconciliation below still lets that snapshot win.
  const hand = useMemo(
    () => (pendingCard !== null && !authoritativeHand.includes(pendingCard)
      ? [...authoritativeHand, pendingCard]
      : authoritativeHand),
    [authoritativeHand, pendingCard],
  );
  const isYourTurn = table.turn === accountId && table.phase === 'playing';

  const [groups, setGroups] = useState<Group[]>([]);
  const [undoStack, setUndoStack] = useState<Group[] | null>(null);
  const [finishMode, setFinishMode] = useState(false);
  const [finishCard, setFinishCard] = useState<CardId | null>(null);
  const [declarationNotice, setDeclarationNotice] = useState<'draw' | 'turn' | null>(null);
  const [selectedCards, setSelectedCards] = useState<CardId[]>([]);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [pendingDiscard, setPendingDiscard] = useState<CardId | null>(null);
  const [emoteOpen, setEmoteOpen] = useState(false);
  const [highlightsOpen, setHighlightsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [intelOpen, setIntelOpen] = useState(showTableIntel);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [highlights, setHighlights] = useState<TableHighlight[]>([]);
  const [turnRecap, setTurnRecap] = useState<{ handId: string; drew: 'closed' | 'discard' | null; discarded: boolean } | null>(null);
  const [networkQuality, setNetworkQuality] = useState<'good' | 'slow'>('good');
  const [submittedShow, setSubmittedShow] = useState<string | null>(null);
  const [viewport, setViewport] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
  const [entered, setEntered] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(() => document.fullscreenElement !== null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);
  const previousHandSize = useRef(0);
  const wasYourTurn = useRef(false);
  const seenHighlightBatches = useRef(new Set<string>());
  const arrangementTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unsyncedArrangement = useRef<Group[] | null>(null);
  const hasSeenOpenConnection = useRef(connection === 'open');
  const wasDisconnected = useRef(false);
  const historyAtDisconnect = useRef(0);
  const [recoveryNote, setRecoveryNote] = useState<number | null>(null);

  const publicHistory = table.publicHistory ?? table.events;

  const clearCardSelection = useCallback(() => {
    setSelectedCards([]);
    setSelectionResetKey((key) => key + 1);
  }, []);

  // Let the mounted board paint once before its entrance state arrives. This
  // avoids replaying the animation on ordinary state broadcasts.
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    clearCardSelection();
  }, [table.handId, clearCardSelection]);

  /**
   * The server owns the arrangement (§6.3 — group membership persists across
   * turns *and* across reconnection), so the local lanes are reconciled against
   * whatever it last stored rather than kept independently. A newly drawn card
   * arrives in its own lane so it lands in a held state instead of vanishing
   * into an existing group.
   */
  useEffect(() => {
    const drew = hand.length > previousHandSize.current;
    previousHandSize.current = hand.length;
    setGroups((current) => {
      const base = current.length > 0 ? current : (you?.groups ?? [hand]);
      return reconcileGroups(base, hand, { newCardLane: drew });
    });
  }, [hand, you?.groups]);

  useEffect(() => setIntelOpen(showTableIntel), [showTableIntel]);

  useEffect(() => {
    if (!toolsOpen) return;
    const onDown = (event: PointerEvent) => {
      if (!toolsRef.current?.contains(event.target as Node)) setToolsOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [toolsOpen]);

  // Grouping is a private, high-frequency interaction. It should feel
  // entirely local while a short trailing write preserves it across a refresh
  // or reconnection. A move that makes table state public flushes first.
  const flushArrangement = useCallback(() => {
    if (arrangementTimer.current) {
      clearTimeout(arrangementTimer.current);
      arrangementTimer.current = null;
    }
    const arrangement = unsyncedArrangement.current;
    if (!arrangement) return;
    unsyncedArrangement.current = null;
    client.act({ type: 'arrange', groups: arrangement });
  }, [client]);

  useEffect(() => {
    if (connection === 'open') flushArrangement();
  }, [connection, flushArrangement]);

  useEffect(() => {
    const flushBeforeLeaving = () => flushArrangement();
    window.addEventListener('pagehide', flushBeforeLeaving);
    document.addEventListener('visibilitychange', flushBeforeLeaving);
    return () => {
      window.removeEventListener('pagehide', flushBeforeLeaving);
      document.removeEventListener('visibilitychange', flushBeforeLeaving);
    };
  }, [flushArrangement]);

  useEffect(() => () => {
    if (arrangementTimer.current) clearTimeout(arrangementTimer.current);
  }, []);

  useEffect(() => {
    if (connection !== 'open') {
      if (hasSeenOpenConnection.current) {
        wasDisconnected.current = true;
        historyAtDisconnect.current = publicHistory.length;
      }
      return;
    }
    if (!hasSeenOpenConnection.current) {
      hasSeenOpenConnection.current = true;
      return;
    }
    if (!wasDisconnected.current) return;
    wasDisconnected.current = false;
    const missed = Math.max(0, publicHistory.length - historyAtDisconnect.current);
    if (missed === 0) return;
    setRecoveryNote(missed);
    const id = `${table.handId}:catchup:${Date.now()}`;
    interactionMetrics.begin(id, 'reconnect-catchup');
    requestAnimationFrame(() => interactionMetrics.settle(id, 'confirmed'));
    const timer = setTimeout(() => setRecoveryNote(null), 7_000);
    return () => clearTimeout(timer);
  }, [connection, publicHistory.length]);

  useEffect(() => sounds.setEnabled(sound), [sound]);
  useEffect(() => sounds.setAlertsEnabled(turnAlert), [turnAlert]);

  useEffect(() => {
    const network = navigator as Navigator & {
      connection?: { effectiveType?: string; addEventListener?: (type: 'change', listener: () => void) => void; removeEventListener?: (type: 'change', listener: () => void) => void };
    };
    const update = () => setNetworkQuality(['slow-2g', '2g'].includes(network.connection?.effectiveType ?? '') ? 'slow' : 'good');
    update();
    network.connection?.addEventListener?.('change', update);
    return () => network.connection?.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (table.events.length === 0) return;
    for (const event of table.events) {
      if (event.type !== 'drew' && event.type !== 'discarded') continue;
      if (event.player !== accountId) continue;
      if (event.type === 'drew') {
        setTurnRecap({ handId: table.handId, drew: event.source, discarded: false });
      } else if (event.type === 'discarded') {
        setTurnRecap((current) => ({
          handId: table.handId,
          drew: current?.handId === table.handId ? current.drew : null,
          discarded: true,
        }));
      }
    }
  }, [table.events, table.handId, accountId]);

  // A live room should show what just happened, not merely whose card count
  // changed. The events are already privacy-filtered by the server, so this
  // timeline never turns a closed draw into a tell.
  useEffect(() => {
    if (table.events.length === 0) return;
    const batch = `${table.handId}:${JSON.stringify(table.events)}`;
    if (seenHighlightBatches.current.has(batch)) return;
    seenHighlightBatches.current.add(batch);
    if (seenHighlightBatches.current.size > 48) {
      seenHighlightBatches.current = new Set([...seenHighlightBatches.current].slice(-24));
    }
    setHighlights((current) => [
      ...table.events.map((event) => ({ id: `${Date.now()}-${Math.random()}`, event })),
      ...current,
    ].slice(0, 14));
  }, [table.handId, table.events]);

  /**
   * The tray gets a share of the viewport height, not a fixed card size. A
   * phone in landscape has 390px to fit a header, the opponents, the deck, the
   * hand and the actions; the cards have to give.
   */
  useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  const isShowPhase = table.phase === 'show' && table.results === null;
  const isFocusedHandTask = isShowPhase || finishMode;
  const compactLandscape = window.matchMedia(
    '(orientation: landscape) and (pointer: coarse) and (max-height: 540px)',
  ).matches;
  const mobileTray = window.matchMedia('(pointer: coarse) and (max-width: 640px)').matches;
  const sizeFactor = cardSize === 'compact' ? 0.88 : cardSize === 'large' ? 1.16 : 1;
  const baseCardHeight = isFocusedHandTask
    ? compactLandscape
      ? Math.max(60, Math.min(92, Math.round(viewport.height * 0.22)))
      : Math.max(96, Math.min(146, Math.round(viewport.height * 0.36)))
    : compactLandscape
      ? Math.max(48, Math.min(72, Math.round(viewport.height * 0.19)))
      : Math.max(58, Math.min(110, Math.round(viewport.height * 0.26)));
  // Landscape gives the board priority over oversized cards. The large-card
  // preference remains useful, but cannot swallow the stock and opponents.
  const maxCardHeight = Math.round(
    baseCardHeight * (compactLandscape ? Math.min(sizeFactor, 1.08) : sizeFactor),
  );
  // A portrait phone is the one board with height to spare and no width, so
  // its hand may take two rows. Both rows together get about a third of the
  // screen, less the label gap between them.
  const twoRowMaxCardHeight =
    mobileTray && viewport.height > viewport.width
      ? Math.min(maxCardHeight, Math.floor((viewport.height * 0.36 - 34) / 2))
      : null;

  /**
   * §6.5 — three samples, off by default. They are driven from the public
   * events the projection already sends, so what you hear is exactly what the
   * table did, and a card drawn from the closed deck sounds the same whoever
   * drew it (the identity is redacted; the sound is not a tell).
   */
  useEffect(() => {
    if (!sound) return;
    if (table.events.length === 0) return;
    // `table.events` is a fresh array per broadcast, so this runs once per
    // batch; the sound board additionally refuses to retrigger the same sample
    // inside 60ms, which covers a burst arriving in one frame.
    for (const event of table.events) {
      if (event.type === 'dealt') sounds.play('shuffle');
      else if (event.type === 'drew') sounds.play('slide');
      else if (event.type === 'discarded') sounds.play('place');
      else if (event.type === 'declared') sounds.play('declare');
      else if (event.type === 'recycled') sounds.play('shuffle');
    }
  }, [table.events, sound]);

  /**
   * Turn the public events into cards crossing the table. Positions are read
   * from the live DOM, so this follows the layout at whatever breakpoint the
   * player is at rather than assuming one.
   */
  useEffect(() => {
    if (table.events.length === 0) return;
    const centre = (el: Element | null | undefined) => {
      if (!el) return null;
      const box = el.getBoundingClientRect();
      return { x: box.left + box.width / 2 - 39, y: box.top + box.height / 2 - 55 };
    };
    const seatPoint = (id: string) =>
      id === accountId ? centre(trayRef.current) : centre(seatRefs.current.get(id));

    const next: Flight[] = [];
    for (const event of table.events) {
      if (event.type === 'drew') {
        const from = centre(event.source === 'closed' ? deckRef.current : pileRef.current);
        const to = seatPoint(event.player);
        if (from && to) {
          next.push({
            id: ++flightId.current,
            card: event.card,
            wild: event.card !== null && isWild(event.card, ctx),
            from,
            to,
          });
        }
      } else if (event.type === 'discarded') {
        const from = seatPoint(event.player);
        const to = centre(pileRef.current);
        if (from && to) {
          next.push({
            id: ++flightId.current,
            card: event.card,
            wild: isWild(event.card, ctx),
            from,
            to,
          });
        }
      }
    }
    if (next.length === 0) return;
    setFlights((current) => [...current, ...next]);

    // The removal timer deliberately outlives this effect run: the effect
    // re-runs on every broadcast, and cancelling on cleanup would mean cards
    // already in the air are never taken down again.
    const ids = new Set(next.map((f) => f.id));
    const timer = setTimeout(() => {
      flightTimers.current.delete(timer);
      setFlights((current) => current.filter((f) => !ids.has(f.id)));
    }, FLIGHT_MS + 60);
    flightTimers.current.add(timer);
  }, [table.events, accountId, ctx]);

  useEffect(() => {
    const timers = flightTimers.current;
    return () => {
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  useEffect(() => {
    if (isYourTurn && !wasYourTurn.current) {
      // Gated inside the sound board on `turnAlert`, not on `sound`.
      sounds.play('chime');
      // Haptics run independent of the sound settings — a muted phone still
      // gets a tactile nudge that it is your move, not just a badge that is
      // easy to miss if you looked away.
      haptic('turn');
      // A visual badge and a chime both depend on the tab being in front of
      // someone. A multi-hand session can run for minutes; this is the only
      // signal that reaches a player who switched away and would otherwise
      // just lose the turn to the timer.
      if (turnNotifications && document.hidden && typeof Notification !== 'undefined') {
        if (Notification.permission === 'granted') {
          const notification = new Notification(t('table.notify.yourTurn.title'), {
            body: room.label,
            tag: 'rummy-your-turn',
            silent: false,
          });
          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        }
      }
    }
    wasYourTurn.current = isYourTurn;
  }, [isYourTurn, turnNotifications, t, room.label]);

  const hasPendingDraw = pendingDraw?.handId === table.handId;
  const canDraw = isYourTurn && table.awaiting === 'draw' && !hasPendingDraw;
  const canDiscard = isYourTurn && table.awaiting === 'discard';
  const canDrop = canDraw && you?.status === 'active';
  /**
   * Whether this seat still has a move to make in this hand. Deliberately not
   * "am I dropped": a declarer's status flips to `won` the moment they declare,
   * and offering them Declare again for the length of the show window is the
   * same mistake in a different costume.
   */
  const iAmActive = you?.status === 'active';
  const iAmDropped = you?.status === 'dropped' || you?.status === 'auto-dropped';
  const discardCoach = useMemo(() => {
    if (!canDiscard || selectedCards.length !== 1 || hand.length <= table.rules.handSize) return null;
    const selected = selectedCards[0]!;
    const candidates = hand.map((candidate) => ({
      id: candidate,
      points: solveHand(hand.filter((card) => card !== candidate), ctx).points,
    }));
    const selectedScore = candidates.find((candidate) => candidate.id === selected)?.points;
    const bestScore = Math.min(...candidates.map((candidate) => candidate.points));
    if (selectedScore === undefined) return null;
    return { savings: Math.max(0, selectedScore - bestScore) };
  }, [canDiscard, selectedCards, hand, table.rules.handSize, ctx]);
  const handHint = useMemo(() => findHandHint(groups, ctx), [groups, ctx]);
  const groupingReport = useMemo(
    () => evaluateGrouping(groups, hand, ctx),
    [groups, hand, ctx],
  );
  const quickEmote = useMemo(() => {
    if (table.discardTop && isWild(table.discardTop, ctx)) return 'wild';
    if (you?.canDeclare) return 'close';
    return handHint === 'run' ? 'run' : 'nice';
  }, [table.discardTop, ctx, you?.canDeclare, handHint]);

  useEffect(() => {
    const sync = () => setIsFullscreen(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const toggleFullscreen = useCallback(() => {
    haptic('tap');
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
    } else {
      void document.documentElement.requestFullscreen?.().catch(() => {
        /* Fullscreen can be denied outside a user gesture or in an iframe. */
      });
    }
  }, []);

  const pushArrangement = useCallback(
    (next: Group[]) => {
      const startedAt = performance.now();
      setGroups(next);
      unsyncedArrangement.current = next;
      if (arrangementTimer.current) clearTimeout(arrangementTimer.current);
      arrangementTimer.current = setTimeout(flushArrangement, 140);
      const id = `${table.handId}:arrange:${startedAt}`;
      interactionMetrics.begin(id, 'arrange');
      requestAnimationFrame(() => interactionMetrics.settle(id, 'confirmed'));
    },
    [flushArrangement, table.handId],
  );

  const handleSort = useCallback(() => {
    setUndoStack(groups);
    pushArrangement(autoSort(hand, ctx));
  }, [groups, hand, ctx, pushArrangement]);

  // Quick play is an opt-in pace preference, never an assistant making card
  // choices: it only performs the same alphabetical suit/rank sort exposed by
  // the Sort button after this client receives an acknowledgement for *its own*
  // draw. A restored turn after reconnecting must preserve its saved grouping.
  const quickSortAfterDraw = useRef<string | null>(null);
  useEffect(() => {
    if (!quickPlay || pendingDraw?.handId !== table.handId || pendingDraw.card === null) return;
    quickSortAfterDraw.current = pendingDraw.id;
  }, [quickPlay, pendingDraw, table.handId]);

  useEffect(() => {
    if (connection !== 'open') quickSortAfterDraw.current = null;
  }, [connection]);

  useEffect(() => {
    if (!quickPlay || !canDiscard || finishMode || quickSortAfterDraw.current === null) return;
    quickSortAfterDraw.current = null;
    pushArrangement(autoSort(hand, ctx));
  }, [quickPlay, canDiscard, finishMode, hand, ctx, pushArrangement]);

  const handleUndo = useCallback(() => {
    if (!undoStack) return;
    pushArrangement(undoStack);
    setUndoStack(null);
  }, [undoStack, pushArrangement]);

  const handleDiscard = useCallback(
    (card: CardId) => {
      if (finishMode) {
        haptic('tap');
        clearCardSelection();
        setFinishCard(card);
        setFinishMode(false);
        return;
      }
      haptic('tap');
      flushArrangement();
      clearCardSelection();
      client.act({ type: 'discard', card });
    },
    [client, finishMode, clearCardSelection, flushArrangement],
  );

  const requestDiscard = useCallback(
    (card: CardId) => {
      if (finishMode || !confirmDiscard) {
        handleDiscard(card);
        return;
      }
      setPendingDiscard(card);
    },
    [confirmDiscard, finishMode, handleDiscard],
  );

  const declarationGroups = useMemo(
    () => (finishCard ? groups.map((g) => g.filter((c) => c !== finishCard)).filter((g) => g.length > 0) : groups),
    [groups, finishCard],
  );

  const declarationReport = useMemo(() => {
    if (!finishCard) return null;
    return evaluateGrouping(
      declarationGroups,
      hand.filter((c) => c !== finishCard),
      ctx,
    );
  }, [declarationGroups, finishCard, hand, ctx]);

  /** Declare is always in the player's reach; rules only decide what happens
   * after they press it. The finish card remains their choice. */
  const beginDeclaration = useCallback(() => {
    haptic('tap');
    setPendingDiscard(null);
    clearCardSelection();
    if (!canDiscard) {
      setDeclarationNotice(isYourTurn ? 'draw' : 'turn');
      return;
    }
    setDeclarationNotice(null);
    setFinishMode(true);
  }, [canDiscard, isYourTurn, clearCardSelection]);

  const confirmDeclaration = useCallback(() => {
    if (!finishCard) return;
    haptic('success');
    flushArrangement();
    client.act({ type: 'declare', finishCard, groups: declarationGroups });
    setFinishCard(null);
  }, [client, finishCard, declarationGroups, flushArrangement]);

  const splitSelection = useCallback(() => {
    if (selectedCards.length === 0) return;
    const selected = new Set(selectedCards);
    const order = groups.flat().filter((id) => selected.has(id));
    const lane = Math.max(0, groups.findIndex((group) => group.some((id) => selected.has(id))));
    const next = groups.map((group) => group.filter((id) => !selected.has(id))).filter((group) => group.length > 0);
    next.splice(Math.min(lane + 1, next.length), 0, order);
    pushArrangement(next);
    clearCardSelection();
  }, [selectedCards, groups, pushArrangement, clearCardSelection]);

  // Keyboard play across the whole table (§6.3): D draws closed, Shift+D takes
  // the open card, S sorts. The tray owns the arrow keys.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      // A confirmation is a short, focused decision. Letting global shortcuts
      // sort or draw behind it makes the card the player is confirming drift
      // out from under the overlay.
      if (pendingDiscard !== null || finishCard !== null) {
        if (event.key === 'Escape') {
          event.preventDefault();
          setPendingDiscard(null);
          setFinishCard(null);
        }
        return;
      }
      if (event.key === 'd' || event.key === 'D') {
        if (!canDraw) return;
        event.preventDefault();
        haptic('tap');
        client.act({ type: 'draw', source: event.shiftKey ? 'discard' : 'closed' });
      } else if (event.key === 'o' || event.key === 'O') {
        if (!canDraw) return;
        event.preventDefault();
        haptic('tap');
        client.act({ type: 'draw', source: 'discard' });
      } else if (event.key === 's' || event.key === 'S') {
        event.preventDefault();
        handleSort();
      } else if ((event.key === 'x' || event.key === 'X' || event.key === 'Delete') && selectedCards.length === 1) {
        event.preventDefault();
        requestDiscard(selectedCards[0]!);
      } else if (event.key === 'g' || event.key === 'G') {
        event.preventDefault();
        splitSelection();
      } else if (event.key === 'c' || event.key === 'C') {
        event.preventDefault();
        beginDeclaration();
      } else if (event.key === '?') {
        event.preventDefault();
        setShortcutsOpen((open) => !open);
      } else if (event.key === 'Escape') {
        setFinishMode(false);
        setFinishCard(null);
        setEmoteOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canDraw, client, finishCard, handleSort, pendingDiscard, selectedCards, requestDiscard, splitSelection, beginDeclaration]);

  const seats = table.seats;
  /**
   * The arc, rotated so it starts at your own seat (§6.2).
   *
   * It used to be `seats.filter(s => s.id !== accountId)` — everyone but you, in
   * raw server order. That row is missing the one seat the player needs to
   * locate, and the survivors are in an order with no relationship to where the
   * turn is going, so "who plays after me" was unanswerable from the table.
   *
   * Rotating to start at you makes left-to-right the actual turn order and puts
   * your own seat at the head of it: the player immediately right of you is the
   * one who plays next, the one at the far right plays just before you come
   * round again. You appear twice — here as a seat, below as the tray — and that
   * is the point: the seat says where you sit, the tray is where you play.
   */
  const arcSeats = useMemo(() => {
    const mine = seats.findIndex((s) => s.id === accountId);
    if (mine < 0) return seats;
    return seats.map((_, i) => seats[(mine + i) % seats.length]!);
  }, [seats, accountId]);
  /**
   * Turns until each seat plays, counted from the seat on move. Only seats
   * still in the hand are in the cycle — a dropped player is skipped rather
   * than counted, which is what makes "+2" mean two actual turns.
   */
  const turnOrder = useMemo(() => {
    const order = new Map<string, number>();
    const live = seats.filter((seat) => seat.status === 'active' && !seat.eliminated);
    const from = live.findIndex((seat) => seat.id === table.turn);
    if (from < 0) return order;
    for (let i = 0; i < live.length; i++) {
      order.set(live[(from + i) % live.length]!.id, i);
    }
    return order;
  }, [seats, table.turn]);
  const orbits = useMemo(() => {
    const opponents = arcSeats.filter((s) => s.id !== accountId);
    const n = opponents.length;
    const angles =
      ORBIT_ANGLES[n] ?? opponents.map((_, i) => (n < 2 ? 90 : 180 - (180 * i) / (n - 1)));
    const placed = new Map<string, { x: number; y: number }>();
    opponents.forEach((seat, i) => {
      const radians = ((angles[i] ?? 90) * Math.PI) / 180;
      placed.set(seat.id, { x: Math.cos(radians), y: Math.sin(radians) });
    });
    return placed;
  }, [arcSeats, accountId]);
  const turnSeat = seats.find((s) => s.id === table.turn);
  const mySeat = seats.find((s) => s.id === accountId);
  const misses = mySeat?.consecutiveAutoPlays ?? 0;

  const stillArranging = table.showPending
    .map((id) => seats.find((s) => s.id === id)?.handle)
    .filter((name): name is string => Boolean(name));
  const iOweAnArrangement = table.showPending.includes(accountId);

  useEffect(() => {
    if (!canDiscard) clearCardSelection();
  }, [canDiscard, clearCardSelection]);

  useEffect(() => {
    if (canDiscard) setDeclarationNotice(null);
  }, [canDiscard]);

  // §8.1 — the seat is held for exactly as long as the client keeps retrying.
  // "Reconnecting" with no number attached leaves a dropped player guessing
  // whether they have five seconds or five minutes before their seat is gone.
  const reconnectDeadline =
    connection === 'reconnecting' && disconnectedAt > 0 ? disconnectedAt + RECONNECT_GRACE_MS : null;
  const showHandHealth = showGameplayGuidance || expertMode;
  const showAssistance = showGameplayGuidance && !expertMode;
  /*
   * Your turn is already said twice without words: the gold "Your turn" badge
   * and the piles lighting up. The sentence is for someone still learning
   * what a turn is made of, so it follows the guidance setting rather than
   * being a third copy of the same news for everybody.
   */
  const turnGuide = !showAssistance || finishMode
    ? null
    : canDraw
      ? t('table.turnGuide.draw')
      : canDiscard
        ? selectedCards.length === 1
          ? t('table.turnGuide.discardSelected')
          : t('table.turnGuide.discard')
        : null;

  return (
    <div
      className={[
        'table',
        isShowPhase ? 'table--show' : '',
        isShowPhase && iOweAnArrangement ? 'table--show-arranging' : '',
        finishMode ? 'table--declaring' : '',
        isYourTurn ? 'is-your-turn' : '',
        compactLandscape ? 'table--compact-landscape' : '',
        expertMode ? 'table--expert' : '',
        pendingAction ? 'table--action-pending' : '',
        ambientIdle ? 'table--idle' : '',
        entered ? 'is-entered' : 'is-entering',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/*
        Ambient light. A very slow drift across the felt, so the room reads as
        lit rather than printed. Two compositor-only transforms on one element:
        no per-card cost, nothing that reflows, and it disappears entirely
        outside the full motion tier. It holds still while nobody is touching
        the table, because even a cheap endless animation keeps the browser
        producing a frame every refresh.
      */}
      <div className="table__ambient" aria-hidden="true" />

      <header className="table__top">
        <div className="table__title">
          <span className="table__hand-glyph" aria-hidden="true">♠</span>
          <div className="table__hand-copy">
            <h1>
              {table.session?.totalDeals
                ? t('table.handOfTotal', {
                    n: table.ordinal + 1,
                    total: table.session.totalDeals,
                  })
                : t('table.handOf', { n: table.ordinal + 1 })}
            </h1>
            <span className="table__room">{room.label}</span>
          </div>
          {/*
            Every opponent wears their session score on their seat card, and
            your own seat card is the one the table never draws — you are the
            tray at the bottom. Without this you are the only player who cannot
            see where they stand.
          */}
          {table.session !== null && mySeat && (
            <span className="table__you-score">
              <small>{t('table.yourScore')}</small>
              <strong>{formatNumber(mySeat.score)}</strong>
            </span>
          )}
          {room.config.declarationGuard && (
            <span className="tag tag--signal">{t('room.guard')}</span>
          )}
        </div>

        <div className="table__turn" aria-live="polite">
          {table.phase === 'playing' &&
            (isYourTurn ? (
              /*
               * §8.2 — the turn timer is server-authoritative and runs whether
               * or not you are connected, so it is only fair to show it. Others
               * still see the coarse ring and never this number (§7).
               */
              <Countdown until={table.turnEndsAt}>
                {(seconds) => (
                  <span
                    className={`turn-badge${seconds <= 10 ? ' is-urgent' : ''}`}
                    aria-label={t('a11y.turnTimer', { seconds })}
                  >
                    {t('table.yourTurn.timed', { seconds })}
                  </span>
                )}
              </Countdown>
            ) : (
              <span className="turn-badge turn-badge--quiet">
                {t('table.turnOf', { name: turnSeat?.handle ?? '' })}
              </span>
            ))}
        </div>

        {/*
          A wide screen shows every tool as a labelled button. A phone has room
          for three, so React and Leave stay and the rest fold into a labelled
          menu — the alternative was a row of glyphs (⛶ ☷) nobody could name.
          The menu is the same buttons, not a copy of them: on a wide screen
          its wrapper is `display: contents` and simply is not there.
        */}
        <div className="table__tools" ref={toolsRef}>
          <button
            type="button"
            className="btn btn--ghost table__emote-btn"
            onClick={() => setEmoteOpen((v) => !v)}
          >
            {t('table.emote')}
          </button>
          <button
            type="button"
            className="btn btn--ghost table__more-btn"
            onClick={() => setToolsOpen((open) => !open)}
            aria-expanded={toolsOpen}
            aria-controls="table-tools-menu"
          >
            {t('table.more')}
          </button>
          <div
            id="table-tools-menu"
            className={`table__tools-menu${toolsOpen ? ' is-open' : ''}`}
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('button')) setToolsOpen(false);
            }}
          >
            <button
              type="button"
              className="btn btn--ghost table__fullscreen"
              onClick={toggleFullscreen}
              aria-pressed={isFullscreen}
            >
              {isFullscreen ? t('table.fullscreen.exit') : t('table.fullscreen')}
            </button>
            <button
              type="button"
              className="btn btn--ghost table__highlights-btn"
              onClick={() => setHighlightsOpen((open) => !open)}
              aria-expanded={highlightsOpen}
            >
              {t('table.highlights')}
            </button>
            {(table.session?.history.length ?? 0) > 0 && (
              <button
                type="button"
                className="btn btn--ghost table__history-btn"
                onClick={() => setHistoryOpen((open) => !open)}
                aria-expanded={historyOpen}
              >
                {t('table.history')}
              </button>
            )}
            {(showTableIntel || expertMode) && (
              <button
                type="button"
                className="btn btn--ghost table__intel-btn"
                onClick={() => setIntelOpen((open) => !open)}
                aria-expanded={intelOpen}
              >
                {t('table.intel')}
              </button>
            )}
            <button type="button" className="btn btn--ghost table__settings-btn" onClick={onOpenSettings}>
              {t('settings.heading')}
            </button>
          </div>
          <button type="button" className="btn btn--ghost table__leave-btn" onClick={onLeave}>
            {t('table.leave')}
          </button>
        </div>
      </header>

      {(connection !== 'open' || networkQuality === 'slow') && (
        <aside className={`table__recovery${connection !== 'open' ? ' is-reconnecting' : ''}`} role="status">
          <strong>{connection !== 'open' ? t('table.recovery.heading') : t('table.network.slow')}</strong>
          <span>
            {connection !== 'open'
              ? reconnectDeadline !== null
                ? <Countdown until={reconnectDeadline}>{(seconds) => t('table.recovery.copy.timed', { seconds })}</Countdown>
                : t('table.recovery.copy')
              : t('table.network.copy')}
          </span>
          {connection !== 'open' && (
            <button type="button" className="btn btn--small" onClick={() => client.retry()}>
              {t('table.recovery.retry')}
            </button>
          )}
        </aside>
      )}

      {emoteOpen && (
        <div className="emote-bar" role="group" aria-label={t('table.emote')}>
          {Object.entries(EMOTES).map(([key, glyph]) => (
            <button
              key={key}
              type="button"
              className="emote-bar__btn"
              onClick={() => {
                client.emote(key);
                setEmoteOpen(false);
              }}
            >
              {glyph}
            </button>
          ))}
        </div>
      )}

      {/* Always mounted, never visible: the table narrated for anyone who
          cannot see it. The panel below is the same sentences, on demand. */}
      <TableNarration highlights={highlights} seats={table.seats} />

      {highlightsOpen && (
        <TableHighlights highlights={highlights} seats={table.seats} onClose={() => setHighlightsOpen(false)} />
      )}

      {intelOpen && <TableIntel table={table} onClose={() => setIntelOpen(false)} />}

      {historyOpen && table.session && (
        <HandHistory
          session={table.session}
          seats={table.seats}
          accountId={accountId}
          fallbackRules={table.rules}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {shortcutsOpen && (
        <aside className="table__shortcuts" role="status">
          <strong>{t('table.shortcuts')}</strong>
          <span>{t('table.shortcuts.copy')}</span>
        </aside>
      )}

      {table.phase === 'playing' && (
        <button
          type="button"
          className="table__quick-reaction"
          onClick={() => {
            haptic('tap');
            client.emote(quickEmote);
          }}
          aria-label={t(`table.reaction.${quickEmote}` as MessageKey)}
        >
          <span>{EMOTES[quickEmote]}</span>
          <small>{t(`table.reaction.${quickEmote}` as MessageKey)}</small>
        </button>
      )}

      <DealRitual handId={table.handId} enabled={!quickPlay} seats={seats.length} />

      {/*
        The felt: opponents and the three piles. On most screens the wrapper is
        `display: contents` and the two sit in the table's rows as before. On a
        wide screen it becomes the oval, and the seats move out round it.
      */}
      <div className="table__board">
      <div className="table__arc" data-seats={arcSeats.length}>
        {arcSeats.map((seat, i) => (
          <SeatCard
            key={seat.id}
            seat={seat}
            index={i}
            count={arcSeats.length}
            orbit={orbits.get(seat.id)}
            onMount={(el) => {
              if (el) seatRefs.current.set(seat.id, el);
              else seatRefs.current.delete(seat.id);
            }}
            isYou={seat.id === accountId}
            isTurn={table.turn === seat.id}
            turnEndsAt={table.turnEndsAt}
            turnTimer={room.config.turnTimer}
            showScore={table.session !== null}
            arranging={table.showPending.includes(seat.id)}
            publicMove={latestPublicMove(publicHistory, seat.id)}
            turnsAway={turnOrder.get(seat.id) ?? null}
          />
        ))}
      </div>

      <div className="table__centre" ref={deckRef}>
        <CenterStack
          ref={pileRef}
          closedCount={table.closedCount}
          deckCount={table.rules.deckCount}
          discardTop={table.discardTop}
          discardBelow={table.discardBelow}
          wildCard={table.wildCard}
          wildRank={table.wildRank}
          canDraw={canDraw}
          discardMode={canDiscard}
          onDrawClosed={() => {
            haptic('tap');
            client.act({ type: 'draw', source: 'closed' });
          }}
          onDrawDiscard={() => {
            haptic('tap');
            client.act({ type: 'draw', source: 'discard' });
          }}
        />
      </div>
      </div>

      {you === null ? (
        <p className="table__spectating">
          {room.queued.some((seat) => seat.id === accountId) ? t('table.spectating') : t('table.watching')}
        </p>
      ) : (
        <div className="table__bottom" ref={trayRef}>
      {table.phase === 'show' && table.results === null && (
        <div className="banner banner--show" role="status">
          <strong>
            {t('show.winner', {
              name: seats.find((s) => s.status === 'won')?.handle ?? '',
            })}
          </strong>
          {iOweAnArrangement ? (
            <>
              <span>
                <Countdown until={table.showEndsAt}>{(seconds) => t('show.explain', { seconds })}</Countdown>
              </span>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  // The server scores the moment the last arrangement lands, so
                  // this button ends the hand rather than joining a queue.
                  flushArrangement();
                  client.act({ type: 'show', groups });
                  setSubmittedShow(table.handId);
                }}
              >
                {t('show.submit')}
              </button>
            </>
          ) : (
            <span>
              {submittedShow === table.handId ? `${t('show.submitted')} ` : ''}
              {stillArranging.length > 0
                ? t('show.waiting', { names: listFormat(stillArranging) })
                : ''}
            </span>
          )}
        </div>
      )}

          <div className={`table__hand-toolbar${finishMode ? ' table__hand-toolbar--declare' : ''}`}>
            <div className="table__utility">
              <button type="button" className="btn btn--utility" onClick={handleSort}>
                {t('table.sort')}
              </button>
              {undoStack && (
                <button type="button" className="btn btn--ghost btn--utility" onClick={handleUndo}>
                  {t('table.undo')}
                </button>
              )}
            </div>

            <div className="table__status" aria-live="polite">
              {finishMode ? (
                <span className="status status--hint status--declare">{t('declare.finish.pick')}</span>
              ) : turnGuide ? (
                <span className="status status--turn-guide">{turnGuide}</span>
              ) : (
                <span className={you.canDeclare ? 'status status--ready' : 'status'}>
                  {you.canDeclare
                    ? t('table.pointsReady')
                    : t('table.points', { points: you.pointsIfHandEndsNow })}
                </span>
              )}
              {declarationNotice && !finishMode && (
                <span className="status status--hint">
                  {t(declarationNotice === 'draw' ? 'declare.drawFirst' : 'declare.waitTurn')}
                </span>
              )}
              {showAssistance && handHint && !finishMode && !turnGuide && (
                <span className="status status--assist">{t(`table.assist.${handHint}` as MessageKey)}</span>
              )}
              {showAssistance && discardCoach && !finishMode && !turnGuide && (
                <span className={discardCoach.savings > 0 ? 'status status--coach-warn' : 'status status--assist'}>
                  {discardCoach.savings > 0
                    ? t('table.discardCoach.better', { points: discardCoach.savings })
                    : t('table.discardCoach.good')}
                </span>
              )}
              {/* §8.3 — two misses warn, the third is an auto-drop. Say so. */}
              {misses >= 1 && (
                <span className="status status--warn">
                  {t('table.missWarning', { count: misses, points: you.dropCost })}
                </span>
              )}
            </div>

            {showHandHealth && !finishMode && !isShowPhase && <HandProgress report={groupingReport} ctx={ctx} />}

            <div className={`table__utility table__utility--end${finishMode ? ' table__declare-actions' : ''}`}>
              {/*
                A dropped hand is settled: the server answers `not-active` to
                anything it sends (§3.7), so leaving Declare live only produced
                a button that told the player to wait for a turn that is never
                coming back. Say what happened instead.
              */}
              {!iAmActive ? (
                iAmDropped && (
                  <span className="status status--dropped">
                    {/*
                      The penalty conceded, not the hand's deadwood — a drop is
                      scored at its own tier and the cards stop counting. An
                      auto-drop is always the middle tier, even on a seat that
                      never took a turn, so it cannot read `dropCost`.
                    */}
                    {t('table.youDropped', {
                      points:
                        you.status === 'auto-dropped'
                          ? table.rules.scoring.middleDrop
                          : you.dropCost,
                    })}
                  </span>
                )
              ) : (
              <>
              {canDrop && (
                <button
                  type="button"
                  className="btn btn--caution btn--utility"
                  onClick={() => {
                    haptic('tap');
                    client.act({ type: 'drop' });
                  }}
                >
                  {t('table.drop.first', { points: you.dropCost })}
                </button>
              )}
              {finishMode ? (
                <>
                  <button
                    type="button"
                    className="btn btn--ghost btn--utility"
                    onClick={() => {
                      setFinishMode(false);
                      setSelectedCards([]);
                    }}
                  >
                    {t('declare.cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--utility"
                    disabled={selectedCards.length !== 1}
                    onClick={() => {
                      if (selectedCards.length === 1) handleDiscard(selectedCards[0]!);
                    }}
                  >
                    {t('declare.finish.selected')}
                  </button>
                </>
              ) : (
                /*
                  Always in reach, but it only asks for attention when the hand
                  can actually go out. A filled button on every turn of every
                  hand made the riskiest action on the table the loudest one,
                  long before it meant anything.
                */
                <button
                  type="button"
                  className={`btn btn--utility table__declare${
                    you.canDeclare ? (canDiscard ? ' btn--primary btn--ready' : ' is-close') : ''
                  }`}
                  onClick={beginDeclaration}
                >
                  {t('table.declare')}
                </button>
              )}
              </>
              )}
            </div>
          </div>

          <Tray
            groups={groups}
            ctx={ctx}
            onChange={pushArrangement}
            onDiscard={requestDiscard}
            canDiscard={canDiscard || finishMode}
            drawn={you.drawn ?? pendingCard}
            getDiscardRect={() => pileRef.current?.getBoundingClientRect() ?? null}
            onSelectionChange={setSelectedCards}
            selectionLimit={finishMode ? 1 : undefined}
            singleTapSelection={canDiscard && !finishMode}
            selectionResetKey={selectionResetKey}
            onMeldComplete={() => {
              haptic('success');
              sounds.play('meld');
            }}
            disabled={
              table.phase === 'complete' ||
              pendingDiscard !== null ||
              finishCard !== null ||
              (isShowPhase && (!iOweAnArrangement || submittedShow === table.handId))
            }
            maxCardHeight={maxCardHeight}
            twoRowMaxCardHeight={twoRowMaxCardHeight}
            alignment={mobileTray ? 'mobile-leading' : 'center'}
          />

          {/*
            Keep this row mounted for the whole hand. Inserting the button
            only after a card was selected changed the bottom grid's height,
            lifting the tray just as the player was trying to tap Discard.
            An invisible, reserved row keeps the cards in one steady place on
            both desktop and touch layouts.
          */}
          <div
            className={`table__discard-action-slot${
              canDiscard && !finishMode && selectedCards.length === 1 ? ' is-ready' : ''
            }`}
          >
            <div className="table__actions table__actions--context">
              <button
                type="button"
                className="btn btn--discard"
                disabled={!(canDiscard && !finishMode && selectedCards.length === 1)}
                tabIndex={canDiscard && !finishMode && selectedCards.length === 1 ? 0 : -1}
                onClick={() => {
                  if (selectedCards.length === 1) requestDiscard(selectedCards[0]!);
                }}
              >
                <span>{t('table.discard')}</span>
                <small>{t('group.selected', { count: selectedCards.length })}</small>
              </button>
            </div>
          </div>

          {turnRecap?.discarded && (
            <p className="table__turn-recap" role="status">
              {t('table.recap', {
                draw: turnRecap.drew === 'discard' ? t('table.recap.open') : t('table.recap.closed'),
              })}
            </p>
          )}

          {pendingDiscard && (
            <div className="table__discard-confirm" role="alertdialog" aria-label={t('table.discard')}>
              <PlayingCard id={pendingDiscard} wild={isWild(pendingDiscard, ctx)} feature />
              <div className="table__discard-confirm-copy">
                <strong>{t('table.discard')}</strong>
                <div className="table__discard-confirm-actions">
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => {
                      handleDiscard(pendingDiscard);
                      setPendingDiscard(null);
                    }}
                  >
                    {t('table.discard')}
                  </button>
                  <button type="button" className="btn" onClick={() => setPendingDiscard(null)}>
                    {t('declare.cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* §6.4 — declaration is a two-step commitment: the finish card face
          down, then a modal showing the grouping exactly as the server sees it. */}
      {finishCard && declarationReport && (
        <div className="modal modal--declare" role="dialog" aria-modal="true" aria-label={t('declare.heading')}>
          <div className="modal__panel">
            <h2>{t('declare.heading')}</h2>
            <p className="modal__note">{t('declare.explain')}</p>

            <div className="declare__finish">
              <span className="declare__finish-label">{t('declare.finish')}</span>
              <PlayingCard id={finishCard} faceDown />
            </div>

            <div className="declare__groups" dir="ltr">
              {declarationGroups.map((group, i) => (
                <div key={i} className={`declare__group is-${meldClass(declarationReport, i)}`}>
                  {group.map((id) => (
                    <PlayingCard key={id} id={id} wild={isWild(id, ctx)} feature />
                  ))}
                </div>
              ))}
            </div>

            <p className={declarationReport.valid ? 'modal__ok' : 'modal__warn'}>
              {declarationReport.valid ? t('declare.valid') : t('declare.invalid')}
            </p>

            {/*
              Two buttons, not three. This screen has exactly one decision on it
              and the third button — "change the finish card" — was a shortcut
              through a state Cancel already returns you to, on the one screen in
              the product where a mis-click costs 80 points. Cancel now goes back
              to picking the card, which is what someone reconsidering here
              wants anyway.
            */}
            <div className="modal__actions">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setFinishCard(null);
                  setFinishMode(true);
                  clearCardSelection();
                }}
              >
                {t('declare.finish.change')}
              </button>
              <button type="button" className="btn btn--primary" onClick={confirmDeclaration}>
                {t('declare.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {table.results && (
        <HandSummary
          table={table}
          accountId={accountId}
          onContinue={() => client.continueSession()}
          canContinue={!room.config.pauseBetweenHands || room.hostId === accountId}
          waitingForHost={room.config.pauseBetweenHands && room.hostId !== accountId}
        />
      )}

      {table.deckReveal && <DeckVerification table={table} />}

      {recoveryNote !== null && connection === 'open' && (
        <aside className="table__recovery table__recovery--caught-up" role="status">
          <strong>{t('table.recovered', { count: recoveryNote })}</strong>
        </aside>
      )}

      <CardFlights flights={quickPlay ? [] : flights} />

    </div>
  );
}

function HandProgress({
  report,
  ctx,
}: {
  report: ReturnType<typeof evaluateGrouping>;
  ctx: MeldContext;
}) {
  const { t } = useI18n();
  return (
    <div className="table__hand-progress" aria-label={t('table.handProgress')}>
      <span
        className={`hand-progress__item hand-progress__item--pure${
          report.pureSequences >= ctx.config.minPureSequences ? ' is-complete' : ''
        }`}
        title={t('group.pure')}
      >
        <strong>{report.pureSequences}/{ctx.config.minPureSequences}</strong>
        <small>{t('group.pure')}</small>
      </span>
      <span
        className={`hand-progress__item hand-progress__item--sequence${
          report.sequences >= ctx.config.minSequences ? ' is-complete' : ''
        }`}
        title={t('group.impure')}
      >
        <strong>{report.sequences}/{ctx.config.minSequences}</strong>
        <small>{t('group.impure')}</small>
      </span>
      <span className={`hand-progress__item${report.ungrouped.length > 0 ? ' is-pending' : ' is-complete'}`} title={t('group.incomplete')}>
        <strong>{report.ungrouped.length}</strong>
        <small>{t('group.incomplete')}</small>
      </span>
    </div>
  );
}

interface TableHighlight {
  id: string;
  event: TableView['events'][number];
}

/**
 * One event, in words.
 *
 * Lifted out of `TableHighlights` so the same sentence can be spoken as it
 * happens (see `TableNarration`) rather than only being readable inside a panel
 * a sighted player chose to open.
 */
function highlightLine(
  event: TableHighlight['event'],
  seats: TableView['seats'],
  t: ReturnType<typeof useI18n>['t'],
): string {
  const nameOf = (id: string) => seats.find((seat) => seat.id === id)?.handle ?? t('table.someone');
  switch (event.type) {
    case 'dealt': return t('table.highlight.dealt');
    case 'drew': return t(event.source === 'discard' ? 'table.highlight.drewOpen' : 'table.highlight.drewClosed', { name: nameOf(event.player) });
    case 'discarded': return t('table.highlight.discarded', { name: nameOf(event.player) });
    case 'dropped': return t('table.highlight.dropped', { name: nameOf(event.player) });
    case 'declared': return t(event.valid ? 'table.highlight.declared' : 'table.highlight.misdeclared', { name: nameOf(event.player) });
    case 'recycled': return t('table.highlight.recycled');
    case 'completed': return t('table.highlight.completed');
  }
}

/**
 * The table, narrated.
 *
 * Keyboard play was complete and the turn timer was announced, but a player
 * using a screen reader had no way to follow the *table* — who drew from where,
 * what was thrown, who is still arranging. That is not a nice-to-have: it is
 * every piece of information anyone has about anybody else.
 *
 * Polite, so it never interrupts, and one sentence at a time — a live region
 * that re-reads the whole log on every event is worse than silence.
 */
function TableNarration({
  highlights,
  seats,
}: {
  highlights: TableHighlight[];
  seats: TableView['seats'];
}) {
  const { t } = useI18n();
  const latest = highlights[0];
  return (
    <p className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
      {latest ? highlightLine(latest.event, seats, t) : ''}
    </p>
  );
}

function TableHighlights({
  highlights,
  seats,
  onClose,
}: {
  highlights: TableHighlight[];
  seats: TableView['seats'];
  onClose: () => void;
}) {
  const { t } = useI18n();
  const line = (event: TableHighlight['event']) => highlightLine(event, seats, t);

  return (
    <aside className="table__highlights" aria-label={t('table.highlights')}>
      <header>
        <span>{t('table.highlights')}</span>
        <button type="button" onClick={onClose} aria-label={t('settings.close')}>×</button>
      </header>
      {highlights.length === 0 ? (
        <p>{t('table.highlights.empty')}</p>
      ) : (
        <ol>{highlights.map(({ id, event }) => <li key={id}>{line(event)}</li>)}</ol>
      )}
    </aside>
  );
}

/** A locale-correct "A, B and C" without assembling it from fragments (§10). */
function listFormat(names: string[]): string {
  try {
    return new Intl.ListFormat(undefined, { style: 'long', type: 'conjunction' }).format(names);
  } catch {
    return names.join(', ');
  }
}

function meldClass(
  report: ReturnType<typeof evaluateGrouping>,
  index: number,
): 'pure' | 'valid' | 'none' {
  const meld = report.melds[index];
  if (!meld) return 'none';
  return meld.kind === 'pure-sequence' ? 'pure' : 'valid';
}
